import { translationKey } from '../i18n';
import { localize } from '../i18n';
import SingleLineText from '../components/ui/SingleLineText';
import InternationalText from '../components/ui/InternationalText';
import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import PageHeader from '../components/PageHeader';

function Section({ title, children }) {
    return (
        <Box sx={{ mb: 3 }}>
            <InternationalText sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
                {title}
            </InternationalText>
            {children}
        </Box>
    );
}

function Body({ children }) {
    return (
        <Typography sx={{ fontSize: '1.1rem', color: '#444', lineHeight: 1.7 }}>
            {children}
        </Typography>
    );
}

function BulletList({ items }) {
    return (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {items.map((item, i) => (
                <Typography key={i} component="li" sx={{ fontSize: '1.1rem', color: '#444', lineHeight: 1.7, mb: 0.3 }}>
                    {localize(item)}
                </Typography>
            ))}
        </Box>
    );
}

export default function PrivacyPage() {
    return (
        <Box sx={{ pb: 10, bgcolor: 'white', minHeight: '100vh' }}>
            <PageHeader title={localize("개인정보 처리방침")} back />

            <Box sx={{ px: 2.5, pt: 1 }}>
                <SingleLineText sx={{ fontSize: '1rem', color: '#999', mb: 3 }}>{localize("시행일: 2026년 10월 13일")}</SingleLineText>
                <Body>{localize("최종 수정: 2026년 10월 20일")}</Body>

                <Body>{localize("\"집요한약알림\" (이하 \"본 앱\")은 이용자의 개인정보를 소중히 여기며, 아래와 같이 개인정보 처리방침을 안내드립니다.")}</Body>

                <Box sx={{ mt: 3 }}>
                    <Link href="/health-data" sx={{ display: 'inline-block', mb: 2, py: 1, minHeight: 44, color: '#776000', fontWeight: 600, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{localize('건강정보 처리 안내')}</Link>

                    <Section title={localize("1. 수집하는 정보")}>
                        <Body>{localize("본 앱은 서비스 제공을 위해 다음 정보를 수집합니다.")}</Body>
                        <BulletList items={[
                            translationKey('기기 식별자(UUID), 앱·운영체제 버전 등 기기 정보'),
                            translationKey('로그인 이용 시 로그인 제공자(카카오·Apple·Google·이메일), 제공자 사용자 식별자, 앱 계정 및 기기 연결 정보'),
                            translationKey('Google 로그인 시 Google SDK가 이메일과 기본 프로필 정보를 받을 수 있습니다. 앱 서버에는 계정 식별자와 본인 확인용 인증 토큰을 전달하며, Google 이메일과 프로필은 계정 DB에 저장하지 않습니다.'),
                            translationKey('이메일 로그인 이용 시 이메일 주소와 인증 확인 정보, 계정 구분용 가림 처리된 이메일 주소(계정 식별자는 이메일 해시로 저장)'),
                            translationKey('앱 잠금의 비밀번호 검증 값은 이 기기의 보안 저장소에 보관하며 서버에 보내지 않습니다. 비밀번호 재설정에는 휴대폰의 생체 인증 또는 화면 잠금 암호를 사용하며 운영체제가 인증을 처리합니다.'),
                            translationKey('로그인·탈퇴 본인 확인 시 인증 토큰, Apple 인증 코드와 Google 연결 해제용 액세스 토큰(확인·연결 해제에 사용하며 DB에 저장하지 않음)'),
                            translationKey('구매 이용 시 스토어·상품·거래 식별자, 구매 검증 및 광고 제거 권한, 환불 상태(카드번호는 수집하지 않음)'),
                            translationKey('푸시 알림 토큰'),
                            translationKey('약 정보 (이름, 복용 시간, 용량)'),
                            translationKey('복용 기록'),
                            translationKey('증상 기록'),
                            translationKey('약 이미지'),
                            translationKey('기기 그룹 및 가족 연결 정보'),
                            translationKey('앱 사용 통계 (페이지 이동, 세션 시간, 기기·브라우저 정보)'),
                            translationKey('서버 접속 IP에서 추정한 최초 서버 등록 국가 코드, 최근 접속 국가 코드와 국가 관측 시각(기기 UUID 및 연결된 앱 계정과 연계)'),
                        ]} />
                    </Section>

                    <Section title={localize("2. 정보 수집 목적")}>
                        <Body>{localize("수집한 정보는 다음 목적으로만 사용됩니다.")}</Body>
                        <BulletList items={[
                            translationKey('복용 알림 서비스 제공'),
                            translationKey('복용 기록 관리'),
                            translationKey('로그인 본인 확인, 계정별 백업 및 기기 간 데이터 동기화, 계정 삭제'),
                            translationKey('앱 잠금과 약 이름 표시 보호 및 휴대폰 소유자 인증을 통한 비밀번호 재설정'),
                            translationKey('구매 검증·복원, 광고 제거 혜택 제공, 환불 반영 및 부정 사용 방지'),
                            translationKey('동의한 처방전·약봉투 사진에서 약 이름과 복용법 추출'),
                            translationKey('고객 문의 및 계정 삭제 요청 처리'),
                            translationKey('기능 이용 현황 분석 및 서비스 품질 개선'),
                            translationKey('가족 및 보호자 공유 기능'),
                            translationKey('국가별 유입 및 이용 현황을 확인하기 위해 서버 접속 IP에서 국가 코드만 판별하여 저장합니다. 이 국가 지표에는 IP 원문을 보관하지 않으며 외부 위치 조회 API로 보내지 않습니다. 서버 접근 로그와 광고·분석 SDK의 IP 처리는 이 국가 지표와 구분됩니다. 접속 국가는 VPN·통신사·여행 등의 영향으로 실제 위치와 다를 수 있습니다.'),
                        ]} />
                    </Section>

                    <Section title={localize("3. 정보 보관 및 보호")}>
                        <BulletList items={[
                            translationKey('모든 데이터는 HTTPS로 암호화하여 전송됩니다.'),
                            translationKey('등록을 완료한 약과 복용 기록은 서버에 저장되며, 접근 권한을 관리합니다.'),
                            translationKey('최초 서버 등록 국가 코드는 첫 등록 시 확인한 값으로 유지하며, 과거 미수집이나 첫 조회 실패를 나중에 확인한 국가로 채우지 않습니다. 최근 접속 국가 코드와 관측 시각은 이후 기기 등록 또는 푸시 토큰 갱신 때 갱신합니다. 실시간 위치 추적이나 위치 이동 이력을 저장하는 기능은 아닙니다.'),
                            translationKey('작성 중인 약 이름·기간·알림 설정은 이어서 작성할 수 있도록 현재 기기에만 임시 보관합니다. 초안을 서버나 다른 기기로 동기화하지 않으며 원본 사진과 분석 동의는 보관하지 않습니다.'),
                            translationKey('계정 탈퇴 시 로그인 연결과 계정의 약·복용·증상 기록, 보관 기록, 가족 연결 및 계정 구매 연결을 삭제합니다.'),
                            translationKey('로그아웃 후 별도로 저장한 기록과 다른 가족의 독립된 계정 기록은 유지됩니다.'),
                            translationKey('삭제된 계정에 이전 기기가 다시 접근하지 못하도록, 계정·기록과 연결되지 않은 기기 UUID와 접근 차단 표시를 보관합니다.'),
                        ]} />
                    </Section>

                    <Section title={localize("4. 제3자 제공")}>
                        <Body>{localize("본 앱은 다음 제3자 서비스를 이용합니다.")}</Body>
                        <Body>{localize('외부 서비스에도 본 개인정보 처리방침과 동등한 수준의 개인정보 보호를 요구합니다. OpenAI와 Google Cloud의 데이터 처리 조건은 목적에 따른 처리, 비밀 유지, 접근 통제와 보안 조치, 정보주체 요청 지원을 규정합니다.')}</Body>
                        <BulletList items={[
                            translationKey('Google AdMob: 비맞춤 광고 제공'),
                            translationKey('Firebase Cloud Storage: 약 이미지 저장'),
                            translationKey('약에 첨부한 사진은 Firebase Cloud Storage의 미국 중부(us-central1) 리전에 저장됩니다. 기기 언어와 접속 국가를 바꾸어도 이 저장 지역은 바뀌지 않습니다.'),
                            translationKey('Google Cloud Vision: 동의한 처방전·약봉투 사진의 글자 인식'),
                            translationKey('OpenAI: AI 기능 처리 및 동의한 처방전·약봉투 사진과 인식된 글자의 약 정보 분석'),
                            translationKey('카카오·Apple·Google: 로그인 본인 확인 및 로그인 연결 해제'),
                            translationKey('Google Play·Apple App Store: 구매·복원 검증 및 환불 처리'),
                            translationKey('Google Analytics for Firebase: 기기 식별자와 앱 사용 통계 수집(서비스 품질 개선 목적). 기기 식별자는 로그인 계정과 연결될 수 있습니다.'),
                            translationKey('새 분석 선택 기능을 지원하는 앱에서 Firebase 분석의 별도 선택 지역은 Google UMP가 확인한 유럽경제지역(EEA)·영국·스위스입니다. 해당 지역에서는 설정의 이용 분석 설정에서 별도로 동의하기 전까지 Firebase 분석을 수집하지 않습니다. 광고 동의와는 별도이며, 동의하지 않아도 앱 기능을 사용할 수 있습니다.'),
                            translationKey('그 밖의 지역에서는 이전에 거절한 선택이 없으면 Firebase 분석을 자동으로 수집합니다. 별도 선택 이력이 없는 이용자에게는 새 선택 창이나 이용 분석 설정 행을 표시하지 않습니다. 이전 거절은 지역을 옮겨도 유지하며, 별도 선택 이력이 있는 이용자는 이용 분석 설정에서 선택을 변경할 수 있습니다.'),
                            translationKey('새 분석 선택 기능을 지원하는 앱에서는 지역을 확인하지 못하거나 선택 저장·분석 SDK 오류가 발생하면 Firebase 분석을 끕니다. 이 기능이 포함된 새 앱 버전은 앱 코드 실행 전에도 수집 차단 설정을 적용합니다. 구버전 앱에 새 앱 코드(JavaScript)가 적용되면 실행 뒤 앱에 포함된 분석 SDK의 수집 중단을 시도하지만, 실행 전 수집까지 막지는 못합니다. 웹사이트 업데이트만으로 기존 구버전 앱의 분석 SDK 수집이 중단되지 않을 수 있으므로 최신 앱 버전으로 업데이트해주세요. 새 웹 분석은 분석 선택 저장을 지원하지 않는 앱과 일반 웹사이트에서는 시작하지 않습니다.'),
                            translationKey('Firebase 분석 선택은 서버의 기기 등록, 복약·알림 데이터 저장 및 서버 자체 기능 이용·국가별 유입과 이용 현황 지표와 별개입니다. Firebase 분석에 동의하지 않아도 이러한 서비스 처리와 서버 지표 수집은 계속됩니다.'),
                        ]} />
                        <Box sx={{ mt: 1 }}>
                            <Body>{localize("위 서비스 외에 이용자의 개인정보를 제3자에게 제공하지 않습니다.")}</Body>
                        </Box>
                    </Section>

                    <Section title={localize("5. 건강 정보 관련 고지")}>
                        <Body>{localize("본 앱은 의료 기기가 아니며, 제공하는 약물 상호작용 정보는 참고용으로만 사용해야 합니다. 의료적 결정은 반드시 의사 또는 약사와 상담하세요.")}</Body>
                    </Section>

                    <Section title={localize("6. 이용자의 권리")}>
                        <BulletList items={[
                            translationKey('이용자는 언제든지 본인의 데이터를 열람, 수정, 삭제할 수 있습니다.'),
                            translationKey('로그인한 이용자는 설정 → 백업 및 동기화 → 계정 탈퇴에서 본인 확인 후 탈퇴할 수 있습니다. 비로그인 이용자는 설정 → 내 데이터 모두 삭제를 이용할 수 있습니다.'),
                            translationKey('계정 탈퇴는 스토어 구매를 자동 환불하지 않습니다. 스토어 구매·환불 기록은 해당 스토어의 정책을 따릅니다.'),
                            translationKey('앱 삭제만으로 서버 계정과 데이터가 삭제되지는 않습니다. 계정 탈퇴 또는 삭제 요청을 이용해주세요.'),
                            translationKey('앱을 설치하지 않아도 계정 및 데이터 삭제 요청 페이지 또는 아래 이메일을 통해 삭제를 요청할 수 있습니다.'),
                        ]} />
                    </Section>

                    <Box sx={{ mb: 3 }}>
                        <Typography component="a" href="/account-deletion" sx={{ color: '#8a7000', fontWeight: 600, display: 'inline-block', maxWidth: '100%', whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.7 }}>{localize("계정 및 데이터 삭제 요청하기")}</Typography>
                    </Box>
                    <Section title={localize("7. 인공지능(AI) 기능 안내")}>
                        <Body>{localize("본 앱은 약 이름 자동 분석, 약물 상호작용 확인, AI 상담과 처방전·약봉투 사진 분석에 OpenAI 서비스를 사용합니다. 사진의 글자 인식에는 Google Cloud Vision도 사용합니다.")}</Body>
                        <Box sx={{ mt: 1 }}>
                            <BulletList items={[
                                translationKey('AI가 제공하는 답변은 참고용이며, 의료 조언이 아닙니다. 복용 결정은 반드시 의사 또는 약사와 상담하세요.'),
                                translationKey('AI 처리를 위해 약 이름, 복용 시간 등 최소한의 정보가 OpenAI에 전송될 수 있으며, 해당 데이터는 OpenAI의 모델 학습에 사용되지 않습니다.'),
                            translationKey('AI 모델 학습에 사용하지 않는다는 것은 전송한 데이터를 즉시 삭제한다는 뜻이 아닙니다. OpenAI의 기본 정책상 입력과 응답이 포함된 남용 방지 기록은 최대 30일간 보관될 수 있으며, 법령이나 안전상 필요한 경우 더 오래 보관될 수 있습니다. 앱에서 기록을 삭제해도 외부 처리자의 보관 기록이 동시에 즉시 삭제되는 것은 아닙니다.'),
                                translationKey('약 자동 채움과 상호작용 확인은 입력한 약 이름·선택한 제품 정보 및 기존 약 이름·성분·시간을, AI 상담은 질문·대화 내용과 등록된 약 이름·용량·복용법·시간·알림 중지 상태를 OpenAI에 전송합니다. 전송 전에 앱에서 항목과 회사를 안내하고 동의를 받습니다. 취소하면 전송하지 않으며 기본 등록·알림은 계속 사용할 수 있습니다.'),
                                translationKey('처방전·약봉투 사진 분석은 사진 선택 화면에서 동의한 경우에만 사진과 인식된 글자를 외부 분석 서비스로 전송합니다. 불필요한 개인정보를 가린 사진을 사용해주세요. 취소하거나 직접 입력할 수 있으며, 새 사진 선택 시마다 다시 동의를 받습니다.'),
                                translationKey('약에 붙이는 일반 사진은 분석용 동의 대상과 구분하며, 일반 사진 추가만으로 외부 AI 분석을 요청하지 않습니다.'),
                                translationKey('이용자는 AI 답변이 부적절하다고 판단되는 경우 신고할 수 있습니다.'),
                            ]} />
                        </Box>
                    </Section>

                    <Section title={localize("8. 데이터 보관 기간")}>
                        <BulletList items={[
                            translationKey('수집된 데이터는 서비스 이용 기간 동안 보관됩니다.'),
                            translationKey('최초·최근 접속 국가 코드와 국가 관측 시각은 서비스 이용 기간 동안 보관하며, 계정 탈퇴 또는 내 데이터 모두 삭제 시 삭제합니다.'),
                            translationKey('약 등록 초안은 마지막 내용 변경부터 7일간 현재 기기에 보관합니다. 저장 결과가 미확정인 요청의 이름·설정도 요청 시점부터 7일 뒤 제거하고, 중복 등록 방지용 최소 요청 정보만 최대 30일 유지합니다. 등록 완료·초안 버리기·계정 또는 기기 기록 변경 시 해당 임시 정보를 정리합니다. 기간이 지난 정보는 앱에서 저장소를 확인할 때 정리됩니다.'),
                            translationKey('이메일 인증 이력은 남용 방지를 위해 인증 만료 후 최대 25시간 동안 보관한 뒤 정리합니다. 계정 탈퇴 시 해당 이메일 인증 이력과 계정 구분용 가림 처리된 주소를 삭제합니다.'),
                            translationKey('이전 버전의 계정 복구 연결 정보는 기존 보관·삭제 기준을 따릅니다. 해제하거나 계정 변경으로 무효화된 서버 연결은 최대 25시간 뒤 정리하며 계정 탈퇴 시 삭제합니다. 기기에 남은 이전 연결 정보는 잠금 끄기 또는 기존 연결 정리 시 삭제합니다.'),
                            translationKey('앱에서 본인 확인을 거쳐 탈퇴하면 계정과 해당 기록을 삭제합니다. 사진은 삭제 후 참조 여부를 확인해 정리하며, 다른 독립 기록에서 사용 중인 사진은 보존합니다. 사진 삭제 재시도가 필요한 경우 정기 정리 작업으로 처리합니다.'),
                            translationKey('클라우드 저장소에서 사진을 삭제한 뒤에도 복구용 사본은 7일간 보관될 수 있습니다. 이 사본은 일반 앱 조회로 열 수 없으며 보관 기간이 지나면 저장소 정책에 따라 영구 삭제됩니다. 참조 확인이나 삭제 재시도로 정리가 대기 중인 기간과는 구분됩니다.'),
                            translationKey('계정 삭제 후에도 이전 기기의 재접속 방지용 최소 UUID 차단 표식과 환불된 거래의 재사용 방지용 최소 환불 이력(알림·거래 식별자, 처리 시각, 취소 상태)은 해당 보안 기능 운영 기간 동안 보관합니다.'),
                            translationKey('구매 토큰 원문과 Apple 서명 영수증·환불 알림 원문은 DB에 저장하지 않습니다. 구매 검증에는 거래 식별자와 구매 토큰의 해시를 사용하며, 탈퇴 시 앱 계정과 구매의 연결을 삭제합니다. 영구 구매의 복원과 취소 여부 확인에 필요한 최소 거래 식별자·토큰 해시·구매 및 환불 상태는 해당 영구 구매의 복원 지원 기간 동안 별도로 보관하며, 삭제된 계정의 약 기록이나 로그인 정보와 연결하지 않습니다.'),
                            translationKey('Apple·Google이 보관하는 스토어 거래 기록은 각 스토어의 보관 정책을 따릅니다.'),
                            translationKey('삭제 요청 이메일은 본인 확인과 요청 처리에 사용합니다. 처리 완료 후 불필요한 첨부자료는 삭제하며, 분쟁 대응에 필요한 경우 보관 사유와 기간을 개별 안내합니다.'),
                        ]} />
                    </Section>

                    <Section title={localize("9. 미성년자 이용")}>
                        <Body>{localize("본 앱은 만 14세 이상부터 이용 가능하며, 만 14세 미만 아동의 개인정보는 법정대리인의 동의 후에만 수집·이용합니다.")}</Body>
                    </Section>

                    <Section title={localize("10. 문의")}>
                        <Body>{localize("개인정보 관련 문의사항은 아래 이메일로 연락해 주세요.")}</Body>
                        <Typography sx={{ fontSize: '1.1rem', color: '#8a7000', fontWeight: 600, mt: 0.5 }}>
                            ebseud6135@gmail.com
                        </Typography>
                    </Section>
                </Box>
            </Box>
        </Box>
    );
}
